// src/utils/yearOverYearCalculations.ts

import { AsinMonthlyData, MetricConfig } from '@/types/asinMonthlyResultsTypes';
import { 
  MonthIdentifier, 
  MonthComparison, 
  YoYComparisonData,
  YoYCalculationParams,
  YoYCalculationResult
} from '@/types/yearOverYearTypes';

/**
 * Get the current month and 3 previous months
 */
export const getCurrentAndPreviousMonths = (): MonthIdentifier[] => {
  const months: MonthIdentifier[] = [];
  const now = new Date();
  
  // Start with current month and go back 3 months
  for (let i = 0; i < 4; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthName = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    
    months.push({
      monthName,
      year,
      fullName: `${monthName} ${year}`,
      abbreviation: formatMonthAbbreviation(monthName, year)
    });
  }
  
  return months;
};

/**
 * Format month abbreviation like "Jun. '25"
 */
export const formatMonthAbbreviation = (monthName: string, year: number): string => {
  const monthAbbreviations: { [key: string]: string } = {
    'January': 'Jan.',
    'February': 'Feb.',
    'March': 'Mar.',
    'April': 'Apr.',
    'May': 'May',
    'June': 'Jun.',
    'July': 'Jul.',
    'August': 'Aug.',
    'September': 'Sep.',
    'October': 'Oct.',
    'November': 'Nov.',
    'December': 'Dec.'
  };
  
  const abbreviated = monthAbbreviations[monthName] || monthName;
  const shortYear = year.toString().slice(-2);
  return `${abbreviated} '${shortYear}`;
};

/**
 * Find corresponding months in the previous year
 */
export const findPreviousYearData = (
  monthIdentifiers: MonthIdentifier[],
  monthlyData: AsinMonthlyData
): MonthIdentifier[] => {
  return monthIdentifiers.map(month => ({
    monthName: month.monthName,
    year: month.year - 1,
    fullName: `${month.monthName} ${month.year - 1}`,
    abbreviation: formatMonthAbbreviation(month.monthName, month.year - 1)
  }));
};

/**
 * Calculate year-over-year change for a specific metric
 */
export const calculateYoYChange = (params: YoYCalculationParams): YoYCalculationResult => {
  const { currentValue, previousValue, metricConfig } = params;
  
  // Handle undefined values
  if (currentValue === undefined && previousValue === undefined) {
    return {
      absoluteChange: undefined,
      percentageChange: undefined,
      formattedDisplay: '-'
    };
  }
  
  if (currentValue === undefined) {
    return {
      absoluteChange: undefined,
      percentageChange: undefined,
      formattedDisplay: 'N/A'
    };
  }
  
  if (previousValue === undefined || previousValue === 0) {
    return {
      absoluteChange: currentValue,
      percentageChange: undefined,
      formattedDisplay: formatYoYDisplay(currentValue, undefined, metricConfig.formatter)
    };
  }
  
  // Calculate changes
  const absoluteChange = currentValue - previousValue;
  const percentageChange = (absoluteChange / previousValue) * 100;
  
  return {
    absoluteChange,
    percentageChange,
    formattedDisplay: formatYoYDisplay(absoluteChange, percentageChange, metricConfig.formatter)
  };
};

/**
 * Format the Year-over-Year display based on metric type
 */
export const formatYoYDisplay = (
  absoluteChange: number | undefined,
  percentageChange: number | undefined,
  formatter: MetricConfig['formatter']
): string => {
  if (absoluteChange === undefined) {
    return '-';
  }
  
  const percentageStr = percentageChange !== undefined
    ? `${percentageChange >= 0 ? '+' : ''}${percentageChange.toFixed(0)}%`
    : '0%';
  
  switch (formatter) {
    case 'currency':
      // Regular currency formatting (whole dollars)
      const currencyStr = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(absoluteChange);
      return `${currencyStr} / ${percentageStr}`;
      
    case 'currencyWithCents':
      // Currency with cents (for PPC Cost Per Click)
      const currencyWithCentsStr = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(absoluteChange);
      return `${currencyWithCentsStr} / ${percentageStr}`;
      
    case 'percentage':
      // For percentage metrics, multiply by 100 and show with one decimal
      const currentPercentageStr = `${(absoluteChange * 100).toFixed(1)}%`;
      return `${currentPercentageStr} / ${percentageStr}`;
      
    case 'wholeNumber':
    default:
      const numberStr = Math.round(absoluteChange).toLocaleString('en-US');
      return `${numberStr} / ${percentageStr}`;
  }
};

/**
 * Process monthly data into Year-over-Year comparison format
 */
export const processYoYData = (
  monthlyData: AsinMonthlyData,
  metricConfigs: MetricConfig[]
): YoYComparisonData | null => {
  if (!monthlyData?.monthlyMetrics) {
    return null;
  }
  
  // Get current and previous months
  const currentMonths = getCurrentAndPreviousMonths();
  const previousYearMonths = findPreviousYearData(currentMonths, monthlyData);
  
  // Build comparison data for each month
  const comparisonMonths: MonthComparison[] = currentMonths.map((currentMonth, index) => {
    const previousMonth = previousYearMonths[index];
    
    // Get data for current and previous year months
    const currentMonthData = monthlyData.monthlyMetrics?.[currentMonth.fullName];
    const previousMonthData = monthlyData.monthlyMetrics?.[previousMonth.fullName];
    
    // Calculate YoY for each metric
    const metrics: { [metricKey: string]: any } = {};
    
    metricConfigs.forEach(metricConfig => {
      const currentValue = currentMonthData?.[metricConfig.key];
      const previousValue = previousMonthData?.[metricConfig.key];
      
      const yoyResult = calculateYoYChange({
        currentValue,
        previousValue,
        metricConfig
      });
      
      metrics[metricConfig.key] = {
        currentValue,
        previousValue,
        absoluteChange: yoyResult.absoluteChange,
        percentageChange: yoyResult.percentageChange,
        formattedDisplay: yoyResult.formattedDisplay
      };
    });
    
    return {
      currentMonth: currentMonth.fullName,
      previousMonth: previousMonth.fullName,
      currentMonthAbbrev: currentMonth.abbreviation,
      metrics
    };
  });
  
  return {
    asin: monthlyData.asin,
    company: monthlyData.company,
    comparisonMonths,
    lastUpdated: monthlyData.lastUpdated
  };
};

/**
 * Check if we have sufficient data for YoY comparison
 */
export const hasYoYData = (comparisonData: YoYComparisonData | null): boolean => {
  if (!comparisonData?.comparisonMonths) {
    return false;
  }
  
  // Check if at least one month has some comparison data
  return comparisonData.comparisonMonths.some(month => 
    Object.values(month.metrics).some(metric => 
      metric.currentValue !== undefined || metric.previousValue !== undefined
    )
  );
};

/**
 * Get relevant months for Year-over-Year comparison (current + 3 previous)
 * Alias for getCurrentAndPreviousMonths for better naming in YoY context
 */
export const getRelevantMonths = (): string[] => {
  const months = getCurrentAndPreviousMonths();
  return months.map(month => month.abbreviation);
};

/**
 * Calculate Year-over-Year comparison for a single ASIN
 */
export interface YearOverYearComparison {
  metric: string;
  month: string;
  currentValue: number | null;
  previousValue: number | null;
  absoluteChange: number | null;
  percentageChange: number | null;
  formattedDisplay: string;
}

/**
 * Calculate year-over-year comparisons for an ASIN
 */
export const calculateYearOverYearComparison = (
  asinData: AsinMonthlyData,
  relevantMonths: string[]
): YearOverYearComparison[] => {
  const comparisons: YearOverYearComparison[] = [];
  
  if (!asinData?.monthlyMetrics) {
    return comparisons;
  }

  // Get all 18 metric configurations to match the existing dashboard
  const metricConfigs: MetricConfig[] = [
    { key: 'totalTraffic', label: 'Total Traffic', formatter: 'wholeNumber' },
    { key: 'totalUnits', label: 'Total Units', formatter: 'wholeNumber' },
    { key: 'totalRevenue', label: 'Total Revenue', formatter: 'currency' },
    { key: 'totalGrossProfit', label: 'Total Gross Profit', formatter: 'currency' },
    { key: 'grossProfitPerDay', label: 'Gross Profit Per Day', formatter: 'currency' },
    { key: 'grossProfitMargin', label: 'Gross Profit Margin', formatter: 'percentage' },
    { key: 'overallConversionRate', label: 'Overall Conversion Rate', formatter: 'percentage' },
    { key: 'ppcImpressions', label: 'PPC Impressions', formatter: 'wholeNumber' },
    { key: 'ppcClicks', label: 'PPC Clicks', formatter: 'wholeNumber' },
    { key: 'ppcSales', label: 'PPC Sales', formatter: 'currency' },
    { key: 'ppcSpend', label: 'PPC Spend', formatter: 'currency' },
    { key: 'ppcOrders', label: 'PPC Orders', formatter: 'wholeNumber' },
    { key: 'ppcUnits', label: 'PPC Units', formatter: 'wholeNumber' },
    { key: 'ppcConversionRate', label: 'PPC Conversion Rate', formatter: 'percentage' },
    { key: 'ppcClickThroughRate', label: 'PPC Click Through Rate', formatter: 'percentage' },
    { key: 'ppcCostPerClick', label: 'PPC Cost Per Click', formatter: 'currencyWithCents' },
    { key: 'acos', label: 'ACoS', formatter: 'percentage' },
    { key: 'tacos', label: 'TACoS', formatter: 'percentage' },
  ];

  relevantMonths.forEach(monthAbbrev => {
    // Convert abbreviation back to full month name for data lookup
    const currentMonths = getCurrentAndPreviousMonths();
    const currentMonth = currentMonths.find(m => m.abbreviation === monthAbbrev);
    
    if (!currentMonth) return;

    const previousMonth = {
      ...currentMonth,
      year: currentMonth.year - 1,
      fullName: `${currentMonth.monthName} ${currentMonth.year - 1}`
    };

    const currentMonthData = asinData.monthlyMetrics?.[currentMonth.fullName];
    const previousMonthData = asinData.monthlyMetrics?.[previousMonth.fullName];

    metricConfigs.forEach(metricConfig => {
      const currentValue = currentMonthData?.[metricConfig.key] ?? null;
      const previousValue = previousMonthData?.[metricConfig.key] ?? null;

      const yoyResult = calculateYoYChange({
        currentValue: currentValue ?? undefined,
        previousValue: previousValue ?? undefined,
        metricConfig
      });

      comparisons.push({
        metric: metricConfig.label,
        month: monthAbbrev,
        currentValue,
        previousValue,
        absoluteChange: yoyResult.absoluteChange ?? null,
        percentageChange: yoyResult.percentageChange ?? null,
        formattedDisplay: yoyResult.formattedDisplay
      });
    });
  });

  return comparisons;
};

/**
 * Format Year-over-Year value for display
 */
export const formatYearOverYearValue = (comparison: YearOverYearComparison): string => {
  return comparison.formattedDisplay;
};